import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { Menu, MenuModule } from 'primeng/menu';
import { SkeletonModule } from 'primeng/skeleton';
import { PaginatorState } from 'primeng/paginator';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { forkJoin, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, finalize } from 'rxjs/operators';

import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { PermissaoService } from '@core/services/permissao.service';
import { Grupo } from './permissao.model';
import { Utilizador } from '@core/models/models';
import { UtilizadorService } from '@core/services/domain-services';
import { GrupoPermissoesDrawerComponent } from './grupo-permissoes-drawer/grupo-permissoes-drawer.component';

@Component({
  selector: 'app-permissoes',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    DrawerModule,
    InputTextModule,
    MenuModule,
    SkeletonModule,
    PaginatorControlComponent,
    TooltipModule,
    EmptyStateComponent,
    GrupoPermissoesDrawerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './permissoes.component.html',
  styleUrl: './permissoes.component.scss',
})
export class PermissoesComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly permissaoSvc = inject(PermissaoService);
  private readonly utilizadorSvc = inject(UtilizadorService);
  private readonly confirmSvc = inject(ConfirmationService);
  private readonly msg = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private readonly searchSubject = new Subject<string>();

  grupos: Grupo[] = [];
  loading = false;
  error = false;
  totalRecords = 0;
  page = 1;
  pageSize = 10;
  searchTerm = '';
  searchInput = '';
  pendingGroupNameFilter = '';
  showGroupFiltersDrawer = false;

  menuItemsMap = new Map<string, MenuItem[]>();
  openedMenu: Menu | null = null;

  showCreateDialog = false;
  showEditDialog = false;
  editingGroup: Grupo | null = null;
  createForm: FormGroup;
  creating = false;
  savingEdit = false;
  deletingGroupId: string | null = null;
  private createSubmitted = false;

  showPermissoesDrawer = false;
  showUtilizadoresDrawer = false;
  grupoSelecionado: Grupo | null = null;
  utilizadores: Utilizador[] = [];
  utilizadoresDoGrupoKeys = new Set<string>();
  loadingUtilizadores = false;
  errorUtilizadores = false;
  utilizadorEmProcessamento: number | null = null;

  constructor() {
    this.createForm = this.fb.group({
      nome: ['', [Validators.required, Validators.minLength(3)]],
    });
  }

  ngOnInit(): void {
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((termo) => {
        this.searchTerm = termo;
        this.page = 1;
        this.loadGrupos();
      });

    this.loadGrupos();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get first(): number {
    return (this.page - 1) * this.pageSize;
  }

  onSearchInput(value: string): void {
    this.searchInput = value;
    this.searchSubject.next(value.trim());
  }

  clearGroupFilters(): void {
    if (!this.searchInput && !this.searchTerm) return;
    this.searchInput = '';
    this.searchTerm = '';
    this.page = 1;
    this.searchSubject.next('');
  }

  openGroupFilters(): void {
    this.pendingGroupNameFilter = this.searchInput;
    this.showGroupFiltersDrawer = true;
  }

  applyGroupFilters(): void {
    this.onSearchInput(this.pendingGroupNameFilter);
    this.showGroupFiltersDrawer = false;
  }

  resetGroupDrawerFilters(): void {
    this.pendingGroupNameFilter = '';
    this.clearGroupFilters();
  }

  handleEmptyAction(): void {
    if (this.searchInput || this.searchTerm) {
      this.clearGroupFilters();
      return;
    }
    this.openCreateDialog();
  }

  getGroupInitials(nome: string): string {
    return (
      String(nome || 'G')
        .split(/[\s_-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((parte) => parte.charAt(0).toUpperCase())
        .join('') || 'G'
    );
  }

  loadGrupos(): void {
    this.loading = true;
    this.error = false;
    this.openedMenu = null;
    this.cdr.markForCheck();

    this.permissaoSvc
      .listarGrupos({
        pageNumber: this.searchTerm ? 1 : this.page,
        pageSize: this.searchTerm ? 1000 : this.pageSize,
        nomePesquisa: this.searchTerm || undefined,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          const gruposEncontrados = res.data || [];
          const termo = this.searchTerm.trim().toLocaleLowerCase();
          const gruposFiltrados = termo
            ? gruposEncontrados.filter((grupo) =>
                String(grupo.nome || '').toLocaleLowerCase().includes(termo),
              )
            : gruposEncontrados;

          this.totalRecords = termo
            ? gruposFiltrados.length
            : res.totalRecords || 0;
          this.grupos = termo
            ? gruposFiltrados.slice(this.first, this.first + this.pageSize)
            : gruposFiltrados;
          this.buildMenuMap();
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Erro ao carregar grupos:', err);
          this.error = true;
          this.grupos = [];
          this.totalRecords = 0;
          this.cdr.markForCheck();
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.page = (event.page ?? 0) + 1;
    this.pageSize = event.rows ?? this.pageSize;
    this.loadGrupos();
  }

  retry(): void {
    this.loadGrupos();
  }

  trackByGrupo(_index: number, grupo: Grupo): string {
    return grupo.id;
  }

  toggleRowMenu(event: Event, menu: Menu): void {
    if (this.openedMenu && this.openedMenu !== menu) {
      this.openedMenu.hide();
    }
    this.openedMenu = menu;
    menu.toggle(event);
  }

  private buildMenuMap(): void {
    this.menuItemsMap.clear();
    for (const grupo of this.grupos) {
      this.menuItemsMap.set(grupo.id, [
        {
          label: 'Gerir permissões',
          icon: 'ri-shield-keyhole-line',
          command: () => this.openPermissoesDrawer(grupo),
        },
        {
          label: 'Gerir utilizadores',
          icon: 'ri-team-line',
          command: () => this.openUtilizadoresDrawer(grupo),
        },
        {
          label: 'Editar',
          icon: 'ri-pencil-line',
          command: () => this.openEditDialog(grupo),
        },
        {
          label: 'Eliminar',
          icon: 'ri-delete-bin-line',
          command: () => this.confirmarEliminarGrupo(grupo),
        },
      ]);
    }
  }

  openEditDialog(grupo: Grupo): void {
    this.editingGroup = grupo;
    this.createForm.reset({ nome: grupo.nome });
    this.showEditDialog = true;
    this.cdr.markForCheck();
  }

  submitEdit(): void {
    if (!this.editingGroup || this.savingEdit) return;
    const control = this.createForm.get('nome');
    const nome = String(control?.value ?? '').trim();
    if (this.createForm.invalid || nome.length < 3) {
      this.createForm.markAllAsTouched();
      return;
    }

    this.savingEdit = true;
    this.permissaoSvc.editarGrupo(this.editingGroup.id, nome)
      .pipe(takeUntil(this.destroy$), finalize(() => {
        this.savingEdit = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.msg.add({ severity: 'success', summary: 'Perfil actualizado', detail: `O perfil foi actualizado para “${nome}”.`, life: 3500 });
          this.showEditDialog = false;
          this.editingGroup = null;
          this.loadGrupos();
        },
        error: (err) => {
          console.error('Erro ao editar grupo:', err);
          this.msg.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível editar o perfil.', life: 5000 });
        },
      });
  }

  confirmarEliminarGrupo(grupo: Grupo): void {
    this.confirmSvc.confirm({
      header: 'Eliminar perfil',
      message: `Tens a certeza de que pretendes eliminar o perfil “${grupo.nome}”?`,
      acceptLabel: 'Sim, eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.eliminarGrupo(grupo),
    });
  }

  private eliminarGrupo(grupo: Grupo): void {
    if (this.deletingGroupId) return;
    this.deletingGroupId = grupo.id;
    this.permissaoSvc.eliminarGrupo(grupo.id)
      .pipe(takeUntil(this.destroy$), finalize(() => {
        this.deletingGroupId = null;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: () => {
          this.msg.add({ severity: 'success', summary: 'Perfil eliminado', detail: `O perfil “${grupo.nome}” foi eliminado.`, life: 3500 });
          if (this.grupos.length === 1 && this.page > 1) this.page--;
          this.loadGrupos();
        },
        error: (err) => {
          console.error('Erro ao eliminar grupo:', err);
          this.msg.add({ severity: 'error', summary: 'Erro', detail: 'Não foi possível eliminar o perfil.', life: 5000 });
        },
      });
  }

  // ── Criar grupo ──────────────────────────────────────────
  openCreateDialog(): void {
    this.createForm.reset({ nome: '' });
    this.showCreateDialog = true;
    this.cdr.markForCheck();
  }

  isCreateFieldInvalid(): boolean {
    const control = this.createForm.get('nome');
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  submitCreate(): void {
    if (this.creating || this.createSubmitted) return;

    const control = this.createForm.get('nome');
    const nome = String(control?.value ?? '').trim();

    if (this.createForm.invalid || nome.length < 3) {
      this.createForm.markAllAsTouched();
      return;
    }

    this.createSubmitted = true;
    this.creating = true;
    this.cdr.markForCheck();

    this.permissaoSvc
      .criarGrupo(nome)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.creating = false;
          this.createSubmitted = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.msg.add({
            severity: 'success',
            summary: 'Grupo criado',
            detail: `"${nome}" foi registado com sucesso.`,
            life: 3000,
          });
          this.showCreateDialog = false;
          this.page = 1;
          this.loadGrupos();
        },
        error: (err) => {
          console.error('Erro ao criar grupo:', err);
          this.msg.add({
            severity: 'error',
            summary: 'Erro',
            detail: 'Não foi possível criar o grupo.',
            life: 5000,
          });
        },
      });
  }

  // ── Drawers ──────────────────────────────────────────────
  openPermissoesDrawer(grupo: Grupo): void {
    this.grupoSelecionado = grupo;
    this.showPermissoesDrawer = true;
    this.cdr.markForCheck();
  }

  openUtilizadoresDrawer(grupo: Grupo): void {
    this.grupoSelecionado = grupo;
    this.showUtilizadoresDrawer = true;
    this.loadUtilizadores();
    this.cdr.markForCheck();
  }

  loadUtilizadores(): void {
    const grupoId = this.grupoSelecionado?.id;
    if (!grupoId) return;
    this.loadingUtilizadores = true;
    this.errorUtilizadores = false;
    this.cdr.markForCheck();
    forkJoin({
      todos: this.utilizadorSvc.getAll({ pageNumber: 1, pageSize: 1000 }),
      membros: this.utilizadorSvc.getAll({ pageNumber: 1, pageSize: 1000, GrupoId: grupoId }),
    })
      .pipe(takeUntil(this.destroy$), finalize(() => {
        this.loadingUtilizadores = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: ({ todos, membros }) => {
          this.utilizadores = todos?.data ?? [];
          this.utilizadoresDoGrupoKeys = new Set<string>(
            (membros?.data ?? []).flatMap((u: Utilizador) => this.getUtilizadorKeys(u)),
          );
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Erro ao carregar utilizadores:', err);
          this.errorUtilizadores = true;
          this.utilizadores = [];
          this.utilizadoresDoGrupoKeys.clear();
          this.cdr.markForCheck();
        },
      });
  }

  isUtilizadorMembro(utilizador: Utilizador): boolean {
    return this.getUtilizadorKeys(utilizador).some((key) => this.utilizadoresDoGrupoKeys.has(key));
  }

  private getUtilizadorKeys(utilizador: Utilizador): string[] {
    const dados = utilizador as Utilizador & Record<string, unknown>;
    return [
      dados.id,
      dados['Id'],
      dados['utilizadorId'],
      dados['UtilizadorId'],
      dados.userId,
      dados['UserId'],
    ]
      .filter((value): value is string | number => value !== null && value !== undefined && value !== '')
      .map(String);
  }

  confirmarRemoverUtilizador(utilizador: Utilizador): void {
    if (!this.grupoSelecionado || !utilizador.id) return;
    const grupo = this.grupoSelecionado;
    this.confirmSvc.confirm({
      header: 'Remover utilizador do perfil',
      message: `Remover ${utilizador.nome} do perfil “${grupo.nome}”?`,
      acceptLabel: 'Sim, remover',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.alterarMembro(utilizador, 'remover'),
    });
  }

  alterarMembro(utilizador: Utilizador, acao: 'adicionar' | 'remover'): void {
    const grupo = this.grupoSelecionado;
    if (!grupo || !utilizador.id || this.utilizadorEmProcessamento !== null) return;
    this.utilizadorEmProcessamento = utilizador.id;
    const request = acao === 'adicionar'
      ? this.permissaoSvc.adicionarUtilizadorAoGrupo(utilizador.id, grupo.id)
      : this.permissaoSvc.removerUtilizadorDoGrupo(utilizador.id, grupo.id);
    request.pipe(takeUntil(this.destroy$), finalize(() => {
      this.utilizadorEmProcessamento = null;
      this.cdr.markForCheck();
    })).subscribe({
      next: () => {
        this.msg.add({
          severity: 'success',
          summary: acao === 'adicionar' ? 'Utilizador adicionado' : 'Utilizador removido',
          detail: `${utilizador.nome} foi ${acao === 'adicionar' ? 'adicionado ao' : 'removido do'} perfil “${grupo.nome}”.`,
          life: 3500,
        });
        this.loadUtilizadores();
      },
      error: (err) => {
        console.error(`Erro ao ${acao} utilizador no grupo:`, err);
        this.msg.add({
          severity: 'error',
          summary: 'Não foi possível concluir a operação',
          detail: `Não foi possível ${acao === 'adicionar' ? 'adicionar o utilizador ao' : 'remover o utilizador do'} perfil.`,
          life: 5000,
        });
      },
    });
  }
}
